/**
 * The frame around the day's scroller, in every host it has.
 *
 * ── ONE head, since 13 September 2026 ───────────────────────────────────────────────────────
 * There were two. `Dzien - pelny ekran` drew a fullscreen host with its own two rows — a breadcrumb
 * bar carrying Zapisz, then a session bar with the arrows, the name, the counts and a wide load
 * module — and `DayModalHead` was that drawing. The owner's day MODAL
 * (`Builder planu - modal - progresja`) is not that design: it is this file's `DayPanelHead`, the
 * same 26x24 arrow pair and caption and title and "Zwiń wszystkie" and "Superset" and ✕, in a box
 * 80% of the screen wide instead of 574px. So the second head is gone rather than kept beside a
 * host nothing renders, and the `breadcrumb` it was the only reader of went with it.
 *
 * What the modal adds is one control, in `hostSwitch` — ONE toggle carrying `aria-pressed`, whose
 * name swaps between "Otwórz w oknie" and "Wróć do panelu bocznego". It is not in the mock; it is
 * the owner's requirement over it.
 *
 * ── The panel's FOOT is down to a Save, and the BUILDER passes none (7 September 2026) ───────
 * It carried the scope sentence and a Zapisz. The programator mock saves from the ember button in
 * the screen's top bar and nowhere else, so the builder's panel hands `save={null}` and draws no
 * foot at all — the list gains the height. The sentence is a warning rather than decoration, so it
 * moved under the head rather than going with the bar.
 *
 * The bar itself stays, because the CALENDAR's day editor is the same component in a dock that has
 * no top bar to save from: withholding it there would leave that surface with no way to save at all,
 * and `calendar-gestures.spec.ts` is the safety case for exactly that. `save` was always the switch
 * — a host with nothing to offer passes `null` — so no flag was added to say which host this is.
 *
 * Everything here is chrome. It decides nothing: every string arrives already worded from
 * `model/dayProgramming.ts`, and every control calls a store operation the day form handed it.
 */

import { useId, useRef, useState, type ReactNode } from 'react';

import { renameSession } from '~/entities/plan-draft';
import { muscleColour } from '~/shared/ui/muscleColour';
import { WarningIcon } from '~/shared/ui/AuthIcons';
import ember from '~/shared/ui/emberButton.module.css';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import type {
  DayProgrammingView,
  DayStepperWords,
  MuscleBarView,
  SupersetModeView,
  Translate,
} from '../model/dayProgramming';

import icon from '~/shared/ui/iconButton.module.css';

import styles from './DayChrome.module.css';

const KEY = 'web:planBuilder.day';

/** The plan's Save, handed into whichever host the day is in. */
export interface DayFormSave {
  readonly label: string;
  /** Why it cannot act — a pristine plan, a save in flight. `null` when it can. */
  readonly disabledReason: string | null;
  readonly onSave: () => void;
  /** True while the plan carries unsaved work — the gallery's `--warn` dot and its words. */
  readonly isDirty: boolean;
  readonly unsavedLabel: string;
  /**
   * A sentence drawn at the foot's start, beside the button: what the button's act carries with it.
   *
   * Buddy's draft (2 October 2026) is the one host that hands it: its foot's act is „Otwórz w
   * builderze", and the sentence says the edits made in the panel stay in the draft. Omitted by the
   * calendar's editor, whose foot is the button alone, as it always was.
   *
   * `quiet` is a standing fact, read where it stands and never announced (the host says a routine
   * change once, in its own words); `alert` is a failure, drawn in the danger ink with its glyph and
   * said at once.
   */
  readonly note?: { readonly text: string; readonly tone: 'quiet' | 'alert' };
}

/**
 * The head's host control: the press that draws this same day in the other box.
 *
 * `null` where there IS no other box — the calendar's day editor and the client calendar's viewer
 * are one dock each, and a maximise there would open a modal nothing has built. Absent rather than
 * refused: an icon button with no words has nowhere to carry a reason, and there is no act behind
 * it to explain.
 */
export interface DayHostSwitch {
  /** `maximise` in the panel, `restore` in the modal. Decides the glyph and the name, nothing else. */
  readonly kind: 'maximise' | 'restore';
  readonly onPress: () => void;
}

export interface DayChromeProps {
  readonly view: DayProgrammingView;
  readonly headingId: string;
  readonly save: DayFormSave | null;
  /** "Piątek · Nogi" and "Środa · Pull A", or `null` at either end of the phase. */
  readonly previousDay: { readonly token: string; readonly label: string } | null;
  readonly nextDay: { readonly token: string; readonly label: string } | null;
  /**
   * What the two steps are CALLED. Handed in rather than resolved here, because which noun is right
   * depends on whether the plan has weekdays at all, and that is a decision `model/` makes once.
   */
  readonly stepper: DayStepperWords;
  /**
   * Step the dock to another day of the phase, or `null` where there is no such move.
   *
   * `null` is the calendar's day editor: it is open on ONE session, at one cycle, reached from one
   * card — stepping to another day would leave the cycle behind and quietly change what every number
   * in the table means. The arrows are then not drawn at all rather than drawn refused, because
   * "there is no next day" would be a lie about a phase that has four.
   */
  readonly onSelectDay: ((slotToken: string) => void) | null;
  readonly onClose: () => void;
  readonly t: Translate;
  /** The day is being READ. The name stops being editable; everything else about the head stays. */
  readonly readOnly: boolean;
  /**
   * "PONIEDZIAŁEK · DZIEŃ 1 Z 4" over the name — the programator's own caption, worded by
   * `dayPanelCaption` because the POSITION is a fact about the phase the day node cannot see.
   * `null` where neither a weekday nor a position is known.
   */
  readonly caption: string | null;
  /**
   * The head's two list modes: fold everything, and select for a superset. `null` in a viewer and in
   * any host that offers neither — the controls are then absent rather than refused, because there
   * is no list act behind them to explain.
   */
  readonly listModes: DayListModes | null;
  /** Draw this day in the other host, or `null` in a dock that has no other host. */
  readonly hostSwitch: DayHostSwitch | null;
}

/** The two controls the programator puts on the right of the panel head, and their words. */
export interface DayListModes {
  readonly collapseAllLabel: string;
  /** Which way the chevron points: up while the list is open, because that is where a press sends it. */
  readonly isCollapsedAll: boolean;
  readonly onToggleCollapseAll: () => void;
  readonly supersetLabel: string;
  readonly isSupersetOn: boolean;
  readonly onToggleSuperset: () => void;
}

/** The workout's name: a button that becomes a field, exactly as the week card's heading does. */
function SessionTitle({
  view,
  buttonClass,
  inputClass,
  readOnly,
}: {
  readonly view: DayProgrammingView;
  readonly buttonClass: string;
  readonly inputClass: string;
  readonly readOnly: boolean;
}) {
  const [draftName, setDraftName] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Read as a heading, not as a control that refuses. The name is carried by the host's
  // `aria-labelledby`, so it is announced on arrival either way.
  if (readOnly) return <span className={buttonClass}>{view.title}</span>;

  const closeAndRestoreFocus = () => {
    setDraftName(null);
    // The button does not exist yet at this moment — React has not re-rendered — so focusing it
    // synchronously targets nothing. One frame later it is mounted.
    requestAnimationFrame(() => {
      buttonRef.current?.focus();
    });
  };

  const commit = () => {
    if (draftName === null) return;
    const next = draftName.trim();
    closeAndRestoreFocus();
    // A commit that changes nothing is not sent: opening the field and closing it must not mark the
    // plan unsaved, and re-committing a title that came from the DAY's name would write a session
    // name the trainer never authored.
    if (next === (view.authoredTitle ?? '')) return;
    renameSession(view.slotToken, next);
  };

  if (draftName !== null) {
    return (
      <input
        className={inputClass}
        value={draftName}
        aria-label={view.nameFieldLabel}
        maxLength={view.nameMaxLength}
        autoFocus
        onChange={(event) => {
          setDraftName(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            closeAndRestoreFocus();
          }
        }}
      />
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={buttonClass}
      onClick={() => {
        // Opens on what was AUTHORED, never on the placeholder: starting from "Trening bez nazwy"
        // would offer to save it as a real name on the first keystroke.
        setDraftName(view.authoredTitle ?? '');
      }}
    >
      {view.title}
    </button>
  );
}

/** One day arrow, focusable even when there is nowhere to go, carrying the reason it cannot. */
function StepButton({
  target,
  label,
  glyph,
  noAdjacent,
  extraClass,
  onSelect,
}: {
  readonly target: { readonly token: string; readonly label: string } | null;
  readonly label: string;
  readonly glyph: string;
  readonly noAdjacent: string;
  readonly extraClass?: string | undefined;
  readonly onSelect: ((token: string) => void) | null;
}) {
  if (onSelect === null) return null;
  return (
    <button
      type="button"
      className={`${String(icon.iconButton)} ${String(styles.iconButton)} ${extraClass ?? ''}`}
      /* The reason is in the NAME, not only on `title`: an icon button has no other text, and a
       * `title` is announced by nothing reliably and reachable by no keyboard at all. Same shape as
       * `SetTable`'s `RowButton`, which is where this pattern is argued. */
      aria-label={target === null ? `${label}, ${noAdjacent}` : `${label}: ${target.label}`}
      aria-disabled={target === null ? 'true' : undefined}
      title={target === null ? noAdjacent : target.label}
      onClick={() => {
        if (target !== null) onSelect(target.token);
      }}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

/**
 * The muscle load module.
 *
 * ONE scale since 13 September 2026. It drew a second, wider one — percentage shares beside the
 * points and a caption under the row — for `Dzien - pelny ekran`'s session bar, and that bar left
 * with the old fullscreen host. The day modal draws this same 66px strip, because it draws this
 * same head.
 */
function LoadModule({
  bars,
  title,
}: {
  readonly bars: readonly MuscleBarView[];
  readonly title: string;
}) {
  if (bars.length === 0) return null;
  return (
    <ul className={styles.panelLoad} aria-label={title}>
      {bars.map((bar) => (
        <li key={bar.group} className={styles.panelLoadBar}>
          <span className={styles.panelLoadHead}>
            <span className={styles.panelLoadLabel}>{bar.label}</span>
            <span className={styles.panelLoadValue}>{bar.points}</span>
          </span>
          <span className={styles.panelLoadTrack} aria-hidden="true">
            <i
              className={styles.loadFill}
              style={{ width: `${String(bar.barPercent)}%`, background: muscleColour(bar.group) }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}

function SaveButton({ save, className }: { readonly save: DayFormSave; readonly className: string }) {
  return (
    <button
      type="button"
      className={className}
      // The refusal folded into the NAME when there is one. The button has visible words, so the
      // name has to CONTAIN them (WCAG 2.5.3) and the reason is spoken after them.
      aria-label={save.disabledReason === null ? undefined : `${save.label}, ${save.disabledReason}`}
      aria-disabled={save.disabledReason !== null ? 'true' : undefined}
      title={save.disabledReason ?? save.label}
      onClick={() => {
        if (save.disabledReason === null) save.onSave();
      }}
    >
      {save.label}
    </button>
  );
}

/**
 * ONE glyph in two states: the frame of a screen with a side panel down its right edge, filled while
 * the day IS in that panel and empty once it has moved into the window.
 *
 * It was a pair of drawings — four expand corners in the panel, this frame in the modal — and both
 * halves of that were wrong. Two glyphs on one 28px square read as two different controls that
 * happen to swap, when there is one control with two states; and the corners promise fullscreen,
 * which the box is not — it is a card at 80% of the width with the week still visible around it. A
 * toggle that keeps its shape and moves its FILL says what changed without claiming anything about
 * how big the other box is.
 *
 * The state a screen reader hears is `aria-pressed`, on the button. Pressed is the window.
 */
function HostSwitchMark({ isPanel }: { readonly isPanel: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={styles.hostSwitchIcon}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 4h11A1.5 1.5 0 0 1 17 5.5v9a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5v-9A1.5 1.5 0 0 1 4.5 4Z" />
      <path d="M12 4v12" />
      {isPanel && (
        <path
          className={styles.hostSwitchFill}
          d="M12 4h3.5A1.5 1.5 0 0 1 17 5.5v9a1.5 1.5 0 0 1-1.5 1.5H12Z"
          strokeWidth="0"
        />
      )}
    </svg>
  );
}

/**
 * The press that draws this same day in the other box.
 *
 * ONE toggle rather than a maximise and a restore that replace each other. The two names still swap
 * — "Otwórz w oknie" while the day is in the panel, "Wróć do panelu bocznego" while it is in the
 * window — because the button carries no words and the accessible name is the whole of what a
 * screen reader gets, so a verb alone would say nothing about where the day would land.
 * `aria-pressed` carries the STATE beside it, which a name swap on its own cannot: a reader arriving
 * at the control is told what it currently is, not only what pressing it would do.
 */
function HostSwitchButton({ hostSwitch, t }: { readonly hostSwitch: DayHostSwitch; readonly t: Translate }) {
  const label = t(`${KEY}.${hostSwitch.kind}`);
  const isPanel = hostSwitch.kind === 'maximise';
  return (
    <button
      type="button"
      className={`${String(icon.iconButton)} ${String(styles.iconButton)} ${String(styles.hostSwitch)}`}
      aria-pressed={!isPanel}
      aria-label={label}
      title={label}
      onClick={hostSwitch.onPress}
    >
      <HostSwitchMark isPanel={isPanel} />
    </button>
  );
}

/**
 * The end of the head: the host toggle, then the ✕.
 *
 * One component because these two are the only chrome a day has that needs NO view — which is what
 * lets the missing-day state draw them, and it has to: under the modal's scrim, with pointer
 * dismissal off, a day whose token no longer resolves had no exit at all but the browser's Back.
 */
function DayHeadActions({
  hostSwitch,
  onClose,
  t,
}: {
  readonly hostSwitch: DayHostSwitch | null;
  readonly onClose: () => void;
  readonly t: Translate;
}) {
  return (
    <>
      {/* Immediately before the ✕, which is where the owner asked for it: the two controls that
        * change what this day is drawn IN sit together at the end of the head, and the one that
        * takes it away is last. The mock draws neither — see this file's header. */}
      {hostSwitch !== null && <HostSwitchButton hostSwitch={hostSwitch} t={t} />}
      <button
        type="button"
        className={`${String(icon.iconButton)} ${String(styles.iconButton)} ${String(styles.closeButton)} ${String(styles.panelClose)}`}
        aria-label={t(`${KEY}.close`)}
        onClick={onClose}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </>
  );
}

/**
 * The head of a day that is no longer in the plan — the two controls, and nothing else.
 *
 * A day's token stops resolving while the dock is open in exactly two ways: a link or a restored
 * session naming a day that has been deleted, and a Cmd+Z that takes the phase away under it. The
 * form then has no view to draw a head from, and until 13 September 2026 it drew none: an `h2` and a
 * sentence, inside a modal whose scrim covers the week and whose pointer dismissal is off. A trainer
 * with a mouse had the browser's Back button and nothing else.
 */
export function DayMissingHead({
  headingId,
  title,
  onClose,
  t,
}: {
  readonly headingId: string;
  readonly title: string;
  readonly onClose: () => void;
  readonly t: Translate;
}) {
  return (
    <div className={styles.panelHead}>
      <div className={styles.panelIdentity}>
        {/* `tabIndex={-1}` for the same reason the programmed day's heading carries it: the dock
          * sends its initial focus here by id, whatever state the day is in. */}
        <h2 id={headingId} className={styles.titleRow} tabIndex={-1}>
          <span className={styles.panelTitleButton}>{title}</span>
        </h2>
      </div>
      {/* No host toggle here, and not as a caller's choice: pressing it from an error state would
        * write the sticky default host while the trainer is only trying to get out. The close is
        * the one act this state needs. */}
      <DayHeadActions hostSwitch={null} onClose={onClose} t={t} />
    </div>
  );
}

/** The chain-link the programator puts on its `Superset` toggle. */
function LinkMark() {
  return (
    <svg viewBox="0 0 20 20" className={styles.modeIcon} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 12 6 14a2.8 2.8 0 0 1-4-4l2-2" />
      <path d="m12 8 2-2a2.8 2.8 0 0 1 4 4l-2 2" />
      <path d="M7.5 12.5 12.5 7.5" />
    </svg>
  );
}

/** The chevron on "Zwiń wszystkie", pointing the way the press would move the list. */
function FoldMark({ isUp }: { readonly isUp: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`${String(styles.modeIcon)} ${isUp ? '' : String(styles.modeIconFlipped)}`}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 12 5-5 5 5" />
    </svg>
  );
}

/** The panel host's head row and its subbar. */
export function DayPanelHead({
  view,
  headingId,
  previousDay,
  nextDay,
  stepper,
  onSelectDay,
  onClose,
  save,
  t,
  readOnly,
  caption,
  listModes,
  hostSwitch,
}: DayChromeProps) {
  /**
   * "Edycja dotyczy całej fazy", and the Cmd/Ctrl+S chord under it.
   *
   * The programator draws neither, and nothing the mock does not draw may be visible — so the strip
   * that carried them is gone and the words hang off the day's own heading instead. That is the
   * control they belong to: the warning is about what editing THIS DAY reaches, and the chord is how
   * this day is saved, so a screen reader arriving at the dock hears both after the workout's name
   * and a sighted trainer meets neither.
   */
  const scopeId = useId();
  const describedBy = readOnly ? undefined : scopeId;
  return (
    <>
      <div className={styles.panelHead}>
        <StepButton
          target={previousDay}
          label={stepper.previous}
          glyph="‹"
          noAdjacent={stepper.noAdjacent}
          extraClass={String(styles.panelStep)}
          onSelect={onSelectDay}
        />
        <StepButton
          target={nextDay}
          label={stepper.next}
          glyph="›"
          noAdjacent={stepper.noAdjacent}
          extraClass={String(styles.panelStep)}
          onSelect={onSelectDay}
        />
        <div className={styles.panelIdentity}>
          {caption !== null && <span className={styles.panelEyebrow}>{caption}</span>}
          {/* `tabIndex={-1}` so the dock can send its initial focus here — it names what is open,
            * where the first tabbable control (the ‹ step) would navigate AWAY from it. Not in the
            * tab order: -1 makes it focusable programmatically and nothing more. */}
          <h2 id={headingId} className={styles.titleRow} tabIndex={-1} aria-describedby={describedBy}>
            <SessionTitle
              view={view}
              buttonClass={String(styles.panelTitleButton)}
              inputClass={String(styles.panelTitleInput)}
              readOnly={readOnly}
            />
          </h2>
        </div>
        {/* One word between them, since 16 September 2026 (owner). "Zwiń wszystkie" stays a glyph —
          * the chevron says which way the list is about to move and the words are its NAME and its
          * tooltip — and "Superset" carries the canon's word again: it is the one MODE here that
          * changes what a press on a card does, and a trainer has to be able to read that it is on
          * without hovering the square that turned it on.
          *
          * The two of them stand in a box of their own, which is what the 15 September round bought
          * and this one keeps. They are modes of the LIST below; the host toggle and the ✕ beside
          * them are what the day is drawn IN and the way out of it. The grouping is proximity and
          * nothing else — the tighter of the head's two gaps holds this pair together, the wider one
          * sets the day's own chrome apart. The pill is wider than the square beside it, so the pair
          * no longer reads as two of a kind; the gap is what still says they belong together. */}
        {listModes !== null && (
          <div className={styles.modeGroup}>
            <button
              type="button"
              className={`${String(icon.iconButton)} ${String(styles.iconButton)} ${String(styles.modeToggle)}`}
              aria-label={listModes.collapseAllLabel}
              title={listModes.collapseAllLabel}
              onClick={listModes.onToggleCollapseAll}
            >
              <FoldMark isUp={!listModes.isCollapsedAll} />
            </button>
            {/* `aria-pressed` rather than a second label doing the work: the mode is a toggle, and a
              * screen reader has to hear that it is ON while the bar under the head is up. */}
            <button
              type="button"
              className={`${String(styles.modeButton)} ${String(styles.modeButtonWide)} ${
                listModes.isSupersetOn ? String(styles.modeButtonOn) : ''
              }`}
              aria-pressed={listModes.isSupersetOn}
              onClick={listModes.onToggleSuperset}
            >
              <LinkMark />
              {listModes.supersetLabel}
            </button>
          </div>
        )}
        <DayHeadActions hostSwitch={hostSwitch} onClose={onClose} t={t} />
      </div>

      {/* The subbar the mock draws: the counts on the left, the muscle strip on the right, and
        * nothing between them. The `Boczny panel / Pełny ekran` pair that used to stand here went
        * with the fullscreen host on 7 September 2026 — the mock knows one mode. */}
      <div className={styles.panelSubbar}>
        <span className={styles.counts}>{view.counts}</span>
        <span className={styles.spacer} />
        <LoadModule bars={view.muscles} title={view.muscleTitle} />
      </div>

      {/* The heading's description. Not drawn — see `scopeId` above. The chord is stated only where
        * this panel draws no Save of its own, which is the builder's. Under the modal Base UI hides
        * the top bar behind the popup, so the chord is the only save a trainer inside the day can
        * reach; under the panel the bar is reachable but off the day's own path, and the chord is
        * still the save that belongs to the day. The calendar's dock hands over a `save` and draws
        * its own foot bar. */}
      {!readOnly && (
        <p id={scopeId} className={visuallyHidden.srOnly}>
          {view.scopeNote}
          {save === null && ` ${t('web:planBuilder.saveShortcut')}`}
        </p>
      )}
    </>
  );
}

/**
 * The bar the superset mode opens under the head: what to do, and the two ways out.
 *
 * Under two picks the confirm is `aria-disabled` and keeps its reason — never the `disabled`
 * attribute, which would drop the one control carrying the explanation out of the tab order. It is
 * a real button either way, so a trainer who presses it early hears why rather than finding a dead
 * spot in the bar. The reason is in the accessible NAME as well as on `title`: a `title` reaches a
 * pointer and nothing else, which is the population that does not need it.
 */
export function DaySupersetBar({
  mode,
  onCancel,
  onJoin,
}: {
  readonly mode: SupersetModeView;
  readonly onCancel: () => void;
  readonly onJoin: () => void;
}) {
  return (
    <div className={styles.supersetBar}>
      <span
        className={`${String(styles.supersetText)} ${mode.isReady ? String(styles.supersetTextReady) : ''}`}
        role="status"
      >
        {mode.sentence}
      </span>
      <button type="button" className={styles.supersetCancel} onClick={onCancel}>
        {mode.cancelLabel}
      </button>
      <button
        type="button"
        className={styles.supersetJoin}
        aria-label={
          mode.joinRefusalReason === null ? undefined : `${mode.joinLabel}, ${mode.joinRefusalReason}`
        }
        aria-disabled={mode.joinRefusalReason !== null ? 'true' : undefined}
        title={mode.joinRefusalReason ?? mode.joinLabel}
        onClick={() => {
          if (mode.joinRefusalReason === null) onJoin();
        }}
      >
        {mode.joinLabel}
      </button>
    </div>
  );
}

/**
 * The panel host's foot: Save, and nothing else.
 *
 * ── Who draws it ────────────────────────────────────────────────────────────────────────────
 * Whichever host hands over a `save`. The BUILDER's panel hands `null` since 7 September 2026 —
 * the programator mock saves from the screen's top bar and the panel's copy was a second control
 * for it — so this bar is the calendar day editor's now. That dock has no bar above it, and it is
 * the same focus trap, so without this its edits could not be saved at all.
 *
 * The scope sentence left with the builder's Zapisz: it is a standing fact about the surface rather
 * than something that belongs beside an action, and `DayPanelHead` prints it under the head.
 */
export function DayPanelFoot({ save }: { readonly save: DayFormSave | null }): ReactNode {
  if (save === null) return null;
  return (
    <div className={styles.panelFoot}>
      {save.note === undefined ? (
        <span className={styles.spacer} />
      ) : save.note.tone === 'alert' ? (
        <p className={`${String(styles.footNote)} ${String(styles.footNoteAlert)}`} role="alert">
          <WarningIcon className={styles.footNoteGlyph} />
          <span>{save.note.text}</span>
        </p>
      ) : (
        <p className={styles.footNote}>{save.note.text}</p>
      )}
      <SaveButton save={save} className={`${String(ember.ember)} ${String(styles.save)}`} />
    </div>
  );
}
